import type { Permission } from '@contract-analyzer/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { apiClient } from '@/lib/api';

const EMPTY_PROJECT_PERMISSIONS: readonly Permission[] = [];

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed to load project permissions.';
}

export interface UseProjectPermissionsResult {
  readonly error: string | null;
  readonly isLoading: boolean;
  readonly permissions: readonly Permission[];
  readonly hasPermission: (permission: Permission) => boolean;
  readonly reload: () => Promise<void>;
}

export function useProjectPermissions(projectId: string | null): UseProjectPermissionsResult {
  const [permissions, setPermissions] = useState<readonly Permission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedProjectId, setLoadedProjectId] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(false);
  const loadedProjectIdRef = useRef<string | null>(null);
  const normalizedCurrentProjectId = projectId?.trim() ?? '';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  const load = useCallback(
    async (mode: 'initial' | 'background'): Promise<void> => {
      const normalizedProjectId = projectId?.trim() ?? '';
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (normalizedProjectId === '') {
        loadedProjectIdRef.current = null;
        setPermissions([]);
        setError(null);
        setIsLoading(false);
        setLoadedProjectId(null);
        return;
      }

      // Only the first load for a project drives the blocking spinner.
      // Background revalidations (window focus / tab visibility) keep the
      // current permissions on screen so consumers never flash a skeleton
      // mid-session just because the user switched tabs.
      if (mode === 'initial') {
        setError(null);
        setIsLoading(true);
      }

      try {
        const response = await apiClient.getProjectPermissions(normalizedProjectId);
        if (!mountedRef.current || requestIdRef.current !== requestId) {
          return;
        }
        loadedProjectIdRef.current = normalizedProjectId;
        setPermissions(response.permissions);
        setError(null);
        setIsLoading(false);
        setLoadedProjectId(normalizedProjectId);
      } catch (cause: unknown) {
        if (!mountedRef.current || requestIdRef.current !== requestId) {
          return;
        }
        // A failed background revalidation must not wipe permissions the user
        // is actively relying on or eject them with an error banner; only
        // surface failures when there is no good data loaded for this project.
        if (mode === 'background' && loadedProjectIdRef.current === normalizedProjectId) {
          setIsLoading(false);
          return;
        }
        loadedProjectIdRef.current = normalizedProjectId;
        setPermissions([]);
        setError(toErrorMessage(cause));
        setIsLoading(false);
        setLoadedProjectId(normalizedProjectId);
      }
    },
    [projectId],
  );

  const reload = useCallback((): Promise<void> => load('initial'), [load]);

  useEffect(() => {
    void Promise.resolve().then(() => load('initial'));
  }, [load]);

  useEffect(() => {
    if (projectId === null) {
      return;
    }

    const handleFocus = (): void => {
      void load('background');
    };
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void load('background');
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [projectId, load]);

  const statusBelongsToCurrentProject =
    normalizedCurrentProjectId !== '' && loadedProjectId === normalizedCurrentProjectId;
  const visibleError = statusBelongsToCurrentProject ? error : null;
  const visiblePermissions =
    statusBelongsToCurrentProject && visibleError === null ? permissions : EMPTY_PROJECT_PERMISSIONS;
  const visibleIsLoading =
    normalizedCurrentProjectId === ''
      ? false
      : isLoading || !statusBelongsToCurrentProject;

  const hasPermission = useCallback(
    (permission: Permission): boolean => visiblePermissions.includes(permission),
    [visiblePermissions],
  );

  return {
    error: visibleError,
    hasPermission,
    isLoading: visibleIsLoading,
    permissions: visiblePermissions,
    reload,
  };
}
